import React, { useEffect, useState } from "react";
import { Activity, Search, FileText } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../components/Tables/APITable";
import Input from "../../components/Input/Input";
import Pagination from "../../components/Pagination/Pagination";
import Button from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal.jsx";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./ActivityLogs.module.scss";

const ActivityLogs = () => {
  /* =========================
     STATE
  ========================== */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [detailsModal, setDetailsModal] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [moduleId, setModuleId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  /* =========================
     TABLE COLUMNS
  ========================== */
  const columns = [
    { label: "User", key: "user" },
    { label: "User Activity", key: "activity" },
    { label: "Module", key: "module" },
    { label: "Details", key: "details" },
    { label: "Date & Time", key: "datetime" },
    { label: "IP", key: "ip" },
  ];

  /* =========================
     FETCH ACTIVITY LOGS
  ========================== */
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const response = await apiGet(
          "logging",
          `/activity-logs?page=${currentPage}&limit=${rowsPerPage}&search=${appliedSearch}${
            moduleId ? `&moduleId=${moduleId}` : ""
          }`,
        );

        if (response?.status === "success") {
          const { data, pagination, modules } = response.data;
          setModules(modules || []);
          setLogs(
            data.map((log) => ({
              id: log.id,
              user: log.userName,
              activity: log.activity,
              datetime: log.createdAt,
              module: log.module,
              details: log.details
                ? log.details
                    .replace(/\s*(<br\s*\/?>|\/n|\\n|br)\s*/gi, "\n")
                    .replace(/\n+/g, "\n")
                    .trim()
                : "",
              ip: log.ipAddress,
            })),
          );

          setPagination(pagination);
        }
      } catch (error) {
        console.error("Failed to fetch activity logs", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [currentPage, rowsPerPage, appliedSearch, moduleId]);

  /* RESET PAGE WHEN FILTERS CHANGE */
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, moduleId]);

  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setCurrentPage(1);
  };

  /* =========================
     JSX
  ========================== */
  return (
    <div className={styles["admin-activity-logs__page"]}>
      {/* HEADER */}
      <div className={styles["admin-activity-logs__header"]}>
        <div className={styles["admin-activity-logs__icon-wrapper"]}>
          <Activity size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-activity-logs__title-group"]}>
          <h2 className={styles["admin-activity-logs__title"]}>
            Activity Logs
          </h2>
          <p className={styles["admin-activity-logs__subtitle"]}>
            A complete record of user actions, updates, and system events.
          </p>
        </div>
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-activity-logs__card"]}>
        {/* FILTER SECTION */}
        <div className={styles["admin-activity-logs__filter-bar"]}>
          <div className={styles["admin-activity-logs__search-group"]}>
            <Input
              type="search"
              placeholder="Search Company Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>

          <div className={styles["admin-activity-logs__status-group"]}>
            <Input
              type="select"
              placeholder="All Modules"
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              options={[
                { label: "All Modules", value: "" },
                ...modules.map((m) => ({
                  label: m.name,
                  value: m.id,
                })),
              ]}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-activity-logs__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={logs}
              renderCell={(row, key) => {
                /* DATE & TIME */
                if (key === "datetime") {
                  const datetime = row.datetime;
                  if (!datetime) return "--";
                  const [date, time, meridian] = datetime.split(" ");
                  return (
                    <span>
                      {date}{" "}
                      <span className={styles["admin-activity-logs__time"]}>
                        {time} {meridian}
                      </span>
                    </span>
                  );
                }

                /* DETAILS (Clickable Snippet) */
                if (key === "details") {
                  if (!row.details) {
                    return (
                      <span
                        className={styles["admin-activity-logs__details-empty"]}
                      >
                        No details
                      </span>
                    );
                  }

                  const shortText =
                    row.details.length > 25
                      ? row.details.slice(0, 25) + "..."
                      : row.details;

                  return (
                    <span
                      className={styles["admin-activity-logs__details-link"]}
                      onClick={() => setDetailsModal(row.details)}
                      title="View full details"
                    >
                      <FileText size={14} strokeWidth={2.5} />
                      {shortText}
                    </span>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />

        {/* DETAILS MODAL */}
        {detailsModal && (
          <Modal title="Activity Details" onClose={() => setDetailsModal(null)}>
            <div className={styles["admin-activity-logs__modal-content"]}>
              {detailsModal}
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
